<template>
    <modal-dialog ref="modalDialog" @close="reset" @opened="modalOpened">
        <template v-slot:title>
            {{
                isEdit ? "Edit Item" : isItemPage ? "New Item" : "New Activity"
            }}
        </template>
        <template v-slot:body>
            <div v-show="isItemPage">
                <div
                    class="alert alert-warning"
                    role="alert"
                    v-if="isNegativeBundle"
                >
                    <span>
                        <i class="cus-error"></i>
                        Bundle total can not be less then 0.00 !
                    </span>
                </div>
                <div class="form-group form-row">
                    <label for="item" class="col-md-4 col-form-label"
                        >Item:</label
                    >
                    <div class="col-md-8">
                        <vet-select
                            ref="vet-select-item"
                            id="item"
                            module="bundle"
                            placeholder="Item name (required)"
                            v-model="form.item"
                            name="item"
                            v-validate="'required'"
                            :class="{ 'border-danger': errors.has('item') }"
                        />
                    </div>
                </div>
                <div class="form-group form-row">
                    <label for="quantity" class="col-md-4 col-form-label"
                        >Quantity:</label
                    >
                    <div class="col-md-8">
                        <div class="form-row">
                            <div class="col-md-5">
                                <input
                                    type="number"
                                    class="form-control"
                                    name="quantity_min"
                                    id="quantity_min"
                                    v-model="form.quantity_min"
                                    placeholder="Low (required)"
                                    v-validate="
                                        'required|min_value:0|numericalWithDecimal|decimal:3'
                                    "
                                    :class="{
                                        'border-danger': errors.has(
                                            'quantity_min'
                                        )
                                    }"
                                    @blur="quantityMinBlurred"
                                    @input="calculateTotalPrice"
                                />
                            </div>
                            <div class="col-md-5">
                                <input
                                    type="number"
                                    class="form-control"
                                    name="quantity_max"
                                    id="quantity_max"
                                    v-model="form.quantity_max"
                                    placeholder="High (optional)"
                                    v-validate="
                                        `min_value:${form.quantity_min}|numericalWithDecimal|decimal:3`
                                    "
                                    :class="{
                                        'border-danger': errors.has(
                                            'quantity_max'
                                        )
                                    }"
                                    @blur="quantityMaxBlurred"
                                    @input="calculateTotalPrice"
                                />
                            </div>
                            <div class="col-md-2 d-flex align-items-center">
                                <span>{{ displayUom }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="form-group form-row">
                    <label for="price-option" class="col-md-4 col-form-label"
                        >Price:</label
                    >
                    <div class="col-md-8">
                        <select
                            class="form-control"
                            id="price-option"
                            v-model="form.isCustomPrice"
                            @change="onChangeIsCustomPrice"
                        >
                            <option value="0"
                                >Default price from Inventory</option
                            >
                            <option value="1">Custom price</option>
                        </select>
                    </div>
                </div>
                <div v-if="isUsingCustomPrice" class="form-group form-row">
                    <label for="unit-price" class="col-md-4 col-form-label"
                        >Unit Price:</label
                    >
                    <div class="col-md-4">
                        <input
                            :disabled="!isAllowEditPrice"
                            type="text"
                            class="form-control"
                            id="unit-price-min"
                            v-model="form.unitPrice_min"
                            placeholder="Unit Price (required)"
                            name="unitPrice_min"
                            v-validate="
                                'required|numericalWithDecimal|decimal:2'
                            "
                            :class="{
                                'border-danger': errors.has('unitPrice_min')
                            }"
                            @blur="unitPriceMinBlurred"
                            @input="calculateTotalPrice"
                        />
                    </div>
                    <div class="col-md-4">
                        <input
                            :disabled="!isAllowEditPrice"
                            type="text"
                            class="form-control"
                            id="unit-price-max"
                            v-model="form.unitPrice_max"
                            placeholder="Unit Price (optional)"
                            name="unitPrice_max"
                            v-validate="
                                `min_value:${
                                    form.unitPrice_min
                                }|numericalWithDecimal|decimal:2${
                                    !isEmptyValue(form.quantity_max)
                                        ? '|required'
                                        : ''
                                }`
                            "
                            :class="{
                                'border-danger': errors.has('unitPrice_max')
                            }"
                            @blur="unitPriceMaxBlurred"
                            @input="calculateTotalPrice"
                        />
                    </div>
                </div>
                <div v-if="isUsingCustomPrice" class="form-group form-row">
                    <label for="fees" class="col-md-4 col-form-label"
                        >Fees:</label
                    >
                    <div class="col-md-8">
                        <vet-select-fee
                            :value="form.fees"
                            @input="fees => selectFees(fees)"
                            :item-id="selectedItemId"
                            :key="itemKey"
                        ></vet-select-fee>
                    </div>
                </div>

                <div class="form-group form-row">
                    <label for="print-estimate" class="col-md-4 col-form-label"
                        >Print on Estimate:</label
                    >
                    <div class="col-md-8">
                        <select
                            class="form-control"
                            id="print-estimate"
                            v-model="form.printOnEstimate"
                            @change="onChangeIsPrintOnEstimate"
                        >
                            <option value="1">Yes</option>
                            <option value="0">No</option>
                        </select>
                    </div>
                </div>
                <div class="form-group form-row" v-if="!isEdit">
                    <label for="actions" class="col-md-4 col-form-label"
                        >Actions:</label
                    >
                    <div class="col-md-8">
                        <v-select
                            class="show-actions"
                            v-model="form.actions"
                            multiple
                            name="actions"
                            :label="'label'"
                            placeholder="Actions (optional)"
                            :options="filteredActionOptions"
                        >
                        </v-select>
                    </div>
                </div>
                <div class="form-group form-row">
                    <label for="totalPrice" class="col-md-4 col-form-label"
                        >Total:</label
                    >
                    <div class="col-md-4">
                        <input
                            :readonly="true"
                            :disabled="!isAllowEditTotalPrice"
                            v-model="form.totalPrice_min"
                            type="text"
                            class="form-control"
                            id="totalPrice_min"
                            name="totalPrice_min"
                            placeholder="Total (required)"
                            v-validate="'required|numericalWithDecimal'"
                            :class="{
                                'border-danger': errors.has('totalPrice_min')
                            }"
                        />
                    </div>
                    <div class="col-md-4">
                        <input
                            :readonly="true"
                            :disabled="!isAllowEditTotalPrice"
                            v-model="form.totalPrice_max"
                            type="text"
                            class="form-control"
                            id="totalPrice_max"
                            name="totalPrice_max"
                            placeholder="Total (optional)"
                            v-validate="`numericalWithDecimal`"
                            :class="{
                                'border-danger': errors.has('totalPrice_max')
                            }"
                        />
                    </div>
                </div>
                <div class="form-group form-row" v-if="!isEdit">
                    <label for="add-activity" class="col-md-4 col-form-label"
                        >Add As Activity:</label
                    >
                    <div class="col-md-8">
                        <select
                            v-model="form.addAsActivity"
                            id="add-activity"
                            class="form-control"
                        >
                            <option value="0">No</option>
                            <option value="1">Yes</option>
                        </select>
                    </div>
                </div>
            </div>
            <activity-page
                ref="activityPage"
                v-if="isActivityPage"
                :isEstimate="true"
                :form-data="form.activity"
                @update-form="formActivity => (form.activity = formActivity)"
                @is-save-disabled="state => (saveState.activity = !state)"
                :item="form.item"
            />
        </template>
        <template v-slot:confirmBtn>
            <div class="ml-auto">
                <div v-if="!isSaving">
                    <button
                        v-if="isActivityPage && isAddAsActivity"
                        type="button"
                        class="btn btn-primary mr-2"
                        @click="back"
                    >
                        Back
                    </button>

                    <button
                        v-if="isItemPage && isAddAsActivity"
                        type="button"
                        class="btn btn-primary"
                        @click="next"
                        :disabled="isSaveDisabled"
                    >
                        Next
                    </button>

                    <b-button-group
                        v-if="
                            (isItemPage && !isAddAsActivity) ||
                            (isActivityPage && isAddAsActivity)
                        "
                    >
                        <b-dropdown
                            left
                            split
                            :text="isAddNew ? 'Save + Add New' : 'Save + Done'"
                            variant="primary"
                            :disabled="isSaveDisabled"
                            @click="save(isAddNew)"
                        >
                            <b-dropdown-item @click="save(!isAddNew)">
                                {{
                                    isAddNew ? "Save + Done" : "Save + Add New"
                                }}
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-button-group>
                </div>
                <loader v-if="isSaving" :margin="1"></loader>
            </div>
        </template>
    </modal-dialog>
</template>

<script>
import VetSelect from "@/module/commons/vet-select";
import vSelect from "vue-select";
import vetSelectFee from "@/module/commons/vet-select-fee";
import ItemBundleServices from "@/services/items/items-bundle";
import ModalDialog from "@/components/commons/modal-dialog";
import { isNaN, differenceBy, map, debounce, isNil, isNull } from "lodash";
import Loader from "@/module/commons/loader";
import ActivityPage from "@/components/setting-template/bundle-invoice/modal/activity";

const DELAY_BEFORE_CHANGE = 200; // Has delay to show the value changed
const ITEM_PAGE = "item";
const ACTIVITY_PAGE = "activity";

export default {
    components: {
        VetSelect,
        vSelect,
        vetSelectFee,
        ModalDialog,
        Loader,
        ActivityPage
    },
    props: {},
    data() {
        return {
            page: ITEM_PAGE,
            isNegativeBundle: false,
            loading: {
                item: false,
                totalAndNegative: false,
                initial: true
            },
            saveState: {
                activity: false
            },
            form: {
                item: null,
                id: null,
                quantity_min: 0,
                unitPrice_min: 0,
                totalPrice_min: 0,
                quantity_max: null,
                unitPrice_max: null,
                totalPrice_max: null,
                printOnEstimate: 1,
                isCustomPrice: 0,
                fees: [],
                feesTotal: 0,
                actions: [],
                activity: {},
                addAsActivity: "0"
            },
            isEdit: false,
            isSaving: false,
            itemKey: 0,
            isAddNew: false,
            isAddAsActivity: false
        };
    },
    mounted() {
        this.form.unitPrice_min = parseFloat(this.form.unitPrice_min).toFixed(
            2
        );
        this.form.totalPrice_min = parseFloat(this.form.totalPrice_min).toFixed(
            2
        );
    },
    watch: {
        "form.item": async function (value) {
            if (!this.form.item || !this.editLoaded) {
                return;
            }
            this.itemKey++;
            this.loading.item = true;
            await this.getItemDetails();
            this.changedItem();
            this.loading.item = false;
        },
        "form.addAsActivity"(value) {
            this.isAddAsActivity = value == 1;
        }
    },
    computed: {
        templateId() {
            return this.$store.state.settingsTemplate.details.id;
        },
        isUsingCustomPrice() {
            return this.form.isCustomPrice == "1";
        },
        isItemPage() {
            return this.page === ITEM_PAGE;
        },
        isActivityPage() {
            return this.page === ACTIVITY_PAGE;
        },
        isPrintOnEstimate() {
            return this.form.printOnEstimate == "1";
        },
        isZeroQuantity() {
            return this.form.quantity == 0;
        },
        isAllowEditPrice() {
            return this.isPrintOnEstimate && this.isUsingCustomPrice;
        },
        isAllowEditTotalPrice() {
            return (
                this.isPrintOnEstimate &&
                this.isUsingCustomPrice &&
                !this.isZeroQuantity
            );
        },
        actionOptions() {
            if (!this.form.item) {
                return [];
            }

            return map(this.form.item.actions, (action, id) => {
                return {
                    id: id,
                    label: action
                };
            });
        },
        isSaveDisabled() {
            return (
                this.loading.item ||
                this.loading.totalAndNegative ||
                this.isNegativeBundle ||
                this.isUnitPriceMaxInvalid ||
                this.isQuantityMaxLessThanQuantityMin ||
                this.isBothQuantityIsZero ||
                this.isQuantityMaxHasValueButUnitPriceEmpty ||
                this.errors.items.length > 0
            );
        },
        filteredActionOptions() {
            return differenceBy(this.actionOptions, this.form.actions, "id");
        },
        editLoaded() {
            return !this.isEdit || (this.isEdit && !this.loading.initial);
        },
        displayUom() {
            if (this.form.item?.uom) {
                return this.form.item.uom;
            }
            return "UOM";
        },
        selectedItemId() {
            return this.form.item?.id || null;
        },
        isUnitPriceMaxInvalid() {
            return (
                this.isQuantityMaxHasValueButUnitPriceEmpty ||
                (!this.isEmptyValue(this.form.unitPrice_max) &&
                    parseFloat(this.form.unitPrice_max) <
                        parseFloat(this.form.unitPrice_min))
            );
        },
        isQuantityMaxLessThanQuantityMin() {
            return (
                !this.isEmptyValue(this.form.quantity_max) &&
                parseFloat(this.form.quantity_max) <
                    parseFloat(this.form.quantity_min)
            );
        },
        isBothQuantityIsZero() {
            return (
                parseFloat(this.form.quantity_max) === 0 &&
                parseFloat(this.form.quantity_min) === 0
            );
        },
        isQuantityMaxHasValueButUnitPriceEmpty() {
            return (
                !this.isEmptyValue(this.form.quantity_max) &&
                this.isEmptyValue(this.form.unitPrice_max)
            );
        },
        isActivityPageSaveDisabled() {
            return this.isAddAsActivity && this.saveState.activity === false;
        }
    },
    methods: {
        async quantityMinBlurred() {
            if (this.isQuantityMaxLessThanQuantityMin) {
                this.loading.totalAndNegative = true;
                await this._sleep(DELAY_BEFORE_CHANGE);
                this.form.quantity_max = this.form.quantity_min;
                this.calculateTotalPrice();
            } else if (
                this.isEmptyValue(this.form.quantity_min) ||
                this.form.quantity_min == 0
            ) {
                this.form.fees = [];
            }
        },
        async quantityMaxBlurred() {
            let quantityMaxUpdated = null;
            if (this.isQuantityMaxLessThanQuantityMin) {
                quantityMaxUpdated = this.form.quantity;
            } else if (this.isBothQuantityIsZero) {
                quantityMaxUpdated = 1;
            }

            let unitPriceMaxUpdated = null;
            // Set the unitPrice_max when quantity_max is not empty
            if (this.isQuantityMaxHasValueButUnitPriceEmpty) {
                unitPriceMaxUpdated = this.form.unitPrice;
            }

            if (!isNull(quantityMaxUpdated) || !isNull(unitPriceMaxUpdated)) {
                this.loading.totalAndNegative = true;
                await this._sleep(DELAY_BEFORE_CHANGE);
                if (!isNull(quantityMaxUpdated)) {
                    this.form.quantity_max = quantityMaxUpdated;
                }
                if (!isNull(unitPriceMaxUpdated)) {
                    this.form.unitPrice_max = unitPriceMaxUpdated;
                }

                this.calculateTotalPrice();
            } else {
                this.loading.totalAndNegative = false;
            }
        },
        async unitPriceMinBlurred() {
            this.setUnitPriceMaxAsUnitPriceMin();
        },
        async unitPriceMaxBlurred() {
            this.setUnitPriceMaxAsUnitPriceMin();
        },
        async setUnitPriceMaxAsUnitPriceMin() {
            if (this.isUnitPriceMaxInvalid) {
                this.loading.totalAndNegative = true;
                await this._sleep(DELAY_BEFORE_CHANGE);
                this.form.unitPrice_max = this.form.unitPrice_min;
                this.calculateTotalPrice();
            }
        },
        async selectFees(fees) {
            // Check if fees more than 0 and the quantity is emtpy or 0
            if (
                fees.length > 0 &&
                (this.isEmptyValue(this.form.quantity_min) ||
                    this.form.quantity_min == 0)
            ) {
                await this._sleep(DELAY_BEFORE_CHANGE);
                this.form.fees = [];
            } else {
                this.form.fees = fees;

                if (!this.isEdit || (this.isEdit && this.editLoaded)) {
                    this.calculateTotalPrice();
                }
            }
        },
        onChangeIsPrintOnEstimate() {
            if (!this.form.item) {
                return;
            }

            if (!this.isPrintOnEstimate) {
                this.form.unitPrice_min = (0).toFixed(2);
                this.form.unitPrice_max = (0).toFixed(2);
            }
            this.calculateTotalPrice();
        },
        async onChangeIsCustomPrice() {
            if (!this.form.item) {
                return;
            }
            if (this.isPrintOnEstimate) {
                const unitPrice = this.form.item.unit_price;
                this.form.unitPrice_min = unitPrice;
                this.form.unitPrice_max = unitPrice;
            } else {
                this.form.unitPrice_min = (0).toFixed(2);
                this.form.unitPrice_max = (0).toFixed(2);
            }

            if (this.isUsingCustomPrice) {
                this.form.fees = map(this.form.item.details.fees, fee => {
                    return {
                        id: fee.id,
                        name: fee.label,
                        encoded_id: fee.encoded_id
                    };
                });
                this.form.feesTotal = this.form.item.details.fees_total;
            }

            this.calculateTotalPrice();
        },
        async getTotalPrice() {
            if (!this.form.item) {
                return;
            }

            try {
                const res = await ItemBundleServices.getTotalPrice(
                    this.selectedItemId,
                    {
                        is_calculate_after_hour: true,
                        quantity: this.form.quantity_min || 0,
                        unit_price: this.form.unitPrice_min || 0,
                        quantity_max:
                            this.form.quantity_max || this.form.quantity_min,
                        unit_price_max: this.form.unitPrice_max || 0,
                        is_custom_price: this.form.isCustomPrice,
                        fees: map(this.form.fees, fee => fee.id)
                    }
                );
                if (res.message && res.message.code == 200) {
                    return res.resource;
                } else {
                    throw true;
                }
            } catch (error) {
                console.error("Error when calculate total price");
                this._message("Error when calculate total price", "danger");
            }
        },
        async checkNegativeBundle() {
            if (!this.form.item || !this.form.totalPrice_min) {
                this.loading.totalAndNegative = false;
                return;
            }
            try {
                this.isNegativeBundle = false;
                const res = await ItemBundleServices.checkNegativeBundle(
                    this.selectedItemId,
                    {
                        template_id: this.templateId,
                        element_total: this.form.totalPrice_min
                    }
                );
                if (res.message && res.message.code == 200) {
                    this.isNegativeBundle = res.resource.negative_bundle;
                    return this.isNegativeBundle;
                } else {
                    throw true;
                }
            } catch (error) {
                console.error("Error when check negative bundle");
                this._message("Error when check negative bundle", "danger");
                return true;
            } finally {
                this.loading.totalAndNegative = false;
            }
        },
        isEmptyValue(value) {
            return isNil(value) || value === "" || isNaN(value);
        },
        calculateTotalPrice: debounce(async function () {
            let proceed = true;
            if (
                !this.form.item ||
                this.isEmptyValue(this.form.quantity_min) ||
                this.isEmptyValue(this.form.unitPrice_min) ||
                this.errors.has("quantity_min") ||
                this.errors.has("quantity_max") ||
                this.errors.has("unitPrice_min") ||
                this.errors.has("unitPrice_max") ||
                (this.isEdit && !this.editLoaded && this.isUsingCustomPrice) // if edit and first load, don't calculate total price as it already set by user
            ) {
                proceed = false;
            }

            if (!proceed) {
                this.loading.totalAndNegative = false;
                return;
            }

            this.loading.totalAndNegative = true;
            let totalMin = 0;
            let totalMax = 0;

            /** Calculate total using the API */
            if (this.isPrintOnEstimate) {
                let res = await this.getTotalPrice();
                this.form.feesTotal = res.fee_price;
                totalMin = res.total_price;
                totalMax = res.total_price_max;

                // application/models/orm/TblTemplateElement.php@559
                if (this.form.quantity_min == 0) {
                    this.form.feesTotal = 0;
                }
            }
            /** End - Calculate total using the API */

            this.form.totalPrice_min = totalMin.toFixed(2);
            if (
                this.isUsingCustomPrice &&
                this.isPrintOnEstimate &&
                this.isEmptyValue(this.form.quantity_max) &&
                (this.isEmptyValue(this.form.unitPrice_max) ||
                    this.form.unitPrice_max == 0)
            ) {
                this.form.totalPrice_max = null;
            } else {
                this.form.totalPrice_max = totalMax.toFixed(2);
            }
            this.checkNegativeBundle();
        }, 500),
        reset() {
            // customPrice is set outside of nextTick to prevent vee-validate
            // validate field that not rendered yet by customPrice.
            this.form.isCustomPrice = 0;
            this.isEdit = false;

            if (this.isActivityPage) {
                this.$refs.activityPage.reset();

                this.form.activity = {};
            }

            this.$nextTick(() => {
                this.form.item = null;
                this.form.quantity_min = 0;
                this.form.quantity_max = null;
                this.form.unitPrice_min = (0).toFixed(2);
                this.form.unitPrice_max = (0).toFixed(2);
                this.form.totalPrice_min = (0).toFixed(2);
                this.form.totalPrice_max = (0).toFixed(2);
                this.form.printOnEstimate = 1;
                this.form.fees = [];
                this.form.feesTotal = (0).toFixed(2);
                this.form.actions = [];
                this.form.addAsActivity = "0";
                this.$validator.reset();

                this.page = ITEM_PAGE;
            });
        },
        async changedItem() {
            if (!this.form.item) {
                return;
            }

            let item = this.form.item;
            this.form.quantity_min = this.form.item.dose;

            const unitPrice = parseFloat(item.unit_price).toFixed(2);
            this.form.unitPrice_min = unitPrice;
            this.form.unitPrice_max = unitPrice;
            this.form.fees = map(item.details.fees, fee => {
                return {
                    id: fee.id,
                    name: fee.label,
                    encoded_id: fee.encoded_id
                };
            });
            this.form.feesTotal = item.details.fees_total;
            this.form.actions = this.actionOptions;
            this.form.printOnEstimate =
                item.is_print_on_invoice === "1" ? 1 : 0; // BE send is_print_on_invoice instead of is_print_on_estimate
            this.calculateTotalPrice();
        },
        async getItemDetails() {
            if (!this.form.item) {
                return;
            }

            try {
                const res = await this.$http.get(
                    `${process.env.BARRAMUNDI}/inventory/${this.selectedItemId}`
                );

                if (
                    res.data &&
                    res.data.response &&
                    res.data.response.message &&
                    res.data.response.message.code == 200
                ) {
                    if (!this.form.item) {
                        this.loading.item = false;
                        return;
                    }

                    this.form.item.details = res.data.response.resources;
                    // Set the unit_price using cost_plus_price that been calculated based on the markup fee if after clinic hour
                    const unitPrice = (
                        this.form.item.details.cost_plus_price?.raw_value /
                        this.form.item.dose
                    ).toFixed(2);

                    this.$set(this.form.item, "unit_price", unitPrice);
                } else {
                    throw true;
                }
            } catch (error) {
                console.error(error);
                this._message("Error when fetching item detail", "danger");
            }
        },
        async open(isEdit, formData) {
            let form = {};
            this.isEdit = isEdit ? true : false;
            this.loading.initial = true;
            if (this.isEdit && formData) {
                this.title = "Edit Item";

                // Change the original property in item
                this.$set(formData.item, "label", formData.item.name);
                this.$set(
                    formData.item,
                    "uom",
                    formData.item.measurement_unit.value
                );

                let quantityMax = formData.max_quantity;
                let unitPriceMax = formData.max_unit_price;
                let totalPriceMax = formData.max_total_price;
                if (quantityMax === 0) {
                    quantityMax = null;
                }
                if (unitPriceMax === 0 && isNull(quantityMax)) {
                    unitPriceMax = null;
                } else {
                    unitPriceMax = unitPriceMax.toFixed(2);
                }

                if (
                    !formData.is_custom_price &&
                    (isNull(quantityMax) || quantityMax < formData.quantity)
                ) {
                    // reference: application/views/staff/business/template/sub/element/create_update.php
                    // if price set to default and then check if max quantity is null/less than the min, then set totalPrice_max follow totalPrice_min
                    totalPriceMax = formData.total_price.toFixed(2);
                } else if (
                    formData.is_custom_price &&
                    totalPriceMax === 0 &&
                    isNull(unitPriceMax)
                ) {
                    totalPriceMax = null;
                } else {
                    totalPriceMax = totalPriceMax.toFixed(2);
                }

                form = {
                    item: formData.item,
                    label: formData.item.name,
                    id: formData.id,
                    quantity_min: formData.quantity,
                    unitPrice_min: formData.unit_price.toFixed(2),
                    totalPrice_min: formData.total_price.toFixed(2),
                    quantity_max: quantityMax,
                    unitPrice_max: unitPriceMax,
                    totalPrice_max: totalPriceMax,
                    printOnEstimate: formData.print_on_invoice,
                    isCustomPrice: formData.is_custom_price,
                    fees: _.map(formData.fees, fee => {
                        return {
                            id: fee.id,
                            name: fee.name,
                            encoded_id: fee.encoded_id,
                            price: fee.price
                        };
                    }),
                    feesTotal: formData.fees_total,
                    actions: [],
                    addAsActivity: "0"
                };
                this.form = form;
                this.loading.item = true;
                await this.getItemDetails(); //retrieve the fees and actions
                this.loading.item = false;
            }

            this.$refs.modalDialog.open();
        },
        modalOpened() {
            if (!this.isEdit) {
                this.focus();
            }
            this.$nextTick(() => {
                this.loading.initial = false;
            });
        },
        focus() {
            this.$refs["vet-select-item"].$el.querySelector("input").focus();
        },
        close() {
            this.reset();
            this.$refs.modalDialog.close();
            this.$emit("close");
        },
        async save(isAddNew) {
            let isValid = false;

            if (this.isItemPage && !this.isAddAsActivity) {
                isValid = await this.$validator.validate();
            } else if (this.isActivityPage && this.isAddAsActivity) {
                isValid = await this.$validator.validate();
                isValid = await this.$refs.activityPage.$validator.validateAll();
            }

            // VET-8550 - Add validation to address the issue when users quickly click the save button after changing the quantity or unit price values eventhough the value is invalid.
            // This issue occurred because when on blur event, the function will corrected the value but as users hit save quick and rapidly, the save still triggerred even though the blur event sets a boolean to cancel the save.
            if (
                !isValid ||
                this.isUnitPriceMaxInvalid ||
                this.isQuantityMaxLessThanQuantityMin ||
                this.isBothQuantityIsZero ||
                this.isQuantityMaxHasValueButUnitPriceEmpty
            ) {
                return;
            }

            try {
                this.isSaving = true;
                const params = {
                    id: this.isEdit ? this.form.id : null,
                    itemid: this.selectedItemId,
                    quantity: this.form.quantity_min,
                    unit_price: this.form.unitPrice_min,
                    total_price: this.form.totalPrice_min,
                    max_quantity: this.form.quantity_max,
                    max_unit_price: this.form.unitPrice_max,
                    max_total_price: this.form.totalPrice_max,
                    print_on_invoice: this.form.printOnEstimate,
                    price_option: this.form.isCustomPrice,
                    fees_total: this.form.feesTotal,
                    fees: this.form.fees.map(fee => fee.id),
                    iitem_actions: this.isEdit
                        ? []
                        : this.form.actions.map(action => action.id)
                };
                let res = await this.$http.post(
                    `${process.env.BARRAMUNDI}account/template/bundle/add-item/${this.templateId}`,
                    params
                );

                if (res.data.response && res.data.response.code != 200) {
                    throw new Error(res.data.response.message);
                }

                this._message(res.data.response.message);

                if (this.isActivityPage && this.isAddAsActivity) {
                    await this.$refs.activityPage.save();
                }

                this.$emit("onsuccess");
                this.isAddNew = isAddNew;
                if (isAddNew) {
                    this.reset();
                } else {
                    this.close();
                }
            } catch (error) {
                console.error(error);
                this._message("Error saving data", "danger");
            } finally {
                this.isSaving = false;
            }
        },
        back() {
            if (!this.isActivityPage) {
                return;
            }

            this.page = ITEM_PAGE;
        },
        async next() {
            if (!this.isAddAsActivity || !this.isItemPage) {
                return;
            }

            const isValid = await this.$validator.validate();

            if (!isValid) {
                return;
            }

            this.page = ACTIVITY_PAGE;
        }
    }
};
</script>
